/**
 * ReplayDemo — CAPABILITY TEST (2026-06-11).
 *
 * A recorded / replay-style demo player. It plays a fixed sequence of captured
 * app screens (PNG) and branded frames (SVG) while Ari narrates. It is 100%
 * client-side: NO API calls, NO backend mutations, no records created. It only
 * reads static assets from /demo-replay/. Has play / pause / restart and ends
 * cleanly. This is a proof-of-concept for a recorded-demo architecture — it does
 * NOT replace the live Provider "Demo Mode".
 *
 * Public route: /replay-demo  (also launchable from the Gateway).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Play, Pause, RotateCcw, X, ArrowRight } from 'lucide-react';

// Sequence: Customer request → Ari routes → Provider receives lead → Job created.
// Frames 1 & 4 are REAL captured app screens; 2 & 3 are branded SVG frames.
const FRAMES = [
  {
    id: 'request',
    img: '/demo-replay/frame1_customer_request.png',
    title: 'Customer request submitted',
    narration: "A homeowner — Maria — submits a furnace-repair request from the public RemodConnect page. No account, no friction.",
    ms: 4600,
  },
  {
    id: 'route',
    img: '/demo-replay/frame2_ari_routes.svg',
    title: 'Ari routes the request',
    narration: "Ari instantly matches it to the best-fit local provider — scoring trade, distance and availability in under a second.",
    ms: 4400,
  },
  {
    id: 'lead',
    img: '/demo-replay/frame3_provider_lead.svg',
    title: 'Provider receives the lead',
    narration: "Northside Heating & Air gets the lead with full context — ready to accept in one tap.",
    ms: 4200,
  },
  {
    id: 'job',
    img: '/demo-replay/frame4_job_created.png',
    title: 'Job created',
    narration: "Accepted — the job lands on today's dispatch board, scheduled and assigned. Request to scheduled job, automatically.",
    ms: 5200,
  },
];

const NAVY = '#0A1230';

const ReplayDemo = () => {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [chars, setChars] = useState(0);
  const [done, setDone] = useState(false);
  const advanceRef = useRef(null);
  const typerRef = useRef(null);

  const frame = FRAMES[idx];

  const clearTimers = useCallback(() => {
    if (advanceRef.current) { clearTimeout(advanceRef.current); advanceRef.current = null; }
    if (typerRef.current) { clearInterval(typerRef.current); typerRef.current = null; }
  }, []);

  // Drive typing + auto-advance for the current frame while playing.
  useEffect(() => {
    if (done || !playing) return undefined;
    const text = frame.narration;
    // Type the narration over ~60% of the frame's display time.
    const stepMs = Math.max(18, Math.floor((frame.ms * 0.6) / text.length));
    typerRef.current = setInterval(() => {
      setChars((c) => {
        if (c >= text.length) { clearInterval(typerRef.current); typerRef.current = null; return c; }
        return c + 1;
      });
    }, stepMs);
    advanceRef.current = setTimeout(() => {
      if (idx < FRAMES.length - 1) { setIdx((i) => i + 1); setChars(0); }
      else { setDone(true); setPlaying(false); }
    }, frame.ms);
    return clearTimers;
  }, [idx, playing, done, frame, clearTimers]);

  // When paused, reveal the full narration so it's readable.
  useEffect(() => {
    if (!playing && !done) setChars(frame.narration.length);
  }, [playing, done, frame.narration.length]);

  const restart = () => { clearTimers(); setIdx(0); setChars(0); setDone(false); setPlaying(true); };
  const exit = () => navigate('/');
  const shown = frame.narration.slice(0, chars);

  return (
    <div className="fixed inset-0 z-[10000] flex flex-col" style={{ background: NAVY }} data-testid="replay-demo">
      {/* Banner */}
      <div className="flex items-center justify-center gap-2 px-4 py-2 text-white text-[11px] sm:text-sm font-semibold shrink-0"
        style={{ background: 'linear-gradient(90deg,#172554,#1e3a8a,#172554)' }} data-testid="replay-banner">
        <Sparkles className="w-3.5 h-3.5 text-blue-300 shrink-0" />
        <span className="whitespace-nowrap">RECORDED DEMO — capability test · no live data · no charges</span>
        <button onClick={exit} data-testid="replay-exit-top"
          className="ml-2 sm:ml-3 shrink-0 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors text-[11px]">
          <X className="w-3 h-3" /> Exit
        </button>
      </div>

      {/* Stage — the "screen" */}
      <div className="flex-1 min-h-0 flex items-center justify-center p-3 sm:p-8" data-testid="replay-stage">
        <div className="relative w-full max-w-5xl rounded-2xl overflow-hidden"
          style={{ aspectRatio: '16 / 10', border: '1px solid rgba(96,140,230,0.35)', boxShadow: '0 30px 80px -20px rgba(0,0,0,0.7)', background: '#0B1228' }}>
          {FRAMES.map((f, i) => (
            <img key={f.id} src={f.img} alt={f.title} draggable={false}
              data-testid={i === idx ? 'replay-frame-img' : undefined}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-out"
              style={{ opacity: i === idx ? 1 : 0, transform: i === idx ? 'scale(1.0)' : 'scale(1.02)' }} />
          ))}
          {/* step chip */}
          <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white"
            style={{ background: 'rgba(10,18,48,0.72)', backdropFilter: 'blur(6px)' }} data-testid="replay-step-label">
            Step {idx + 1} of {FRAMES.length} · {frame.title}
          </div>
        </div>
      </div>

      {/* Ari dock */}
      <div className="px-3 sm:px-5 pb-4 pt-1 shrink-0">
        <div className="max-w-5xl mx-auto rounded-2xl px-4 py-3"
          style={{ background: 'rgba(12,26,64,0.92)', border: '1px solid rgba(96,140,230,0.4)', boxShadow: '0 -8px 40px -10px rgba(0,0,0,0.5)' }}>
          {/* progress */}
          <div className="flex gap-1 mb-2.5" data-testid="replay-stepper">
            {FRAMES.map((f, i) => (
              <div key={f.id} className="h-1 flex-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.12)' }}>
                <div className="h-full rounded-full" style={{ width: i <= idx || done ? '100%' : '0%', background: '#60A5FA', opacity: (i < idx || done) ? 0.6 : i === idx ? 1 : 0 }} />
              </div>))}
          </div>

          {done ? (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="replay-end">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#3B82F633' }}>
                  <Sparkles className="w-5 h-5 text-blue-300" />
                </div>
                <div className="text-white text-sm sm:text-[15px]">
                  <span className="font-bold">That's the request-to-job flow.</span> Recorded replay — no live API calls, no records created.
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={restart} data-testid="replay-replay-btn"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-bold text-white text-sm transition-transform hover:scale-105" style={{ background: '#3B82F6' }}>
                  <RotateCcw className="w-4 h-4" /> Replay
                </button>
                <button onClick={exit} data-testid="replay-exit-btn" className="px-3 py-2 rounded-full text-sm text-white/75 hover:bg-white/10 transition-colors inline-flex items-center gap-1.5">
                  Back to Gateway <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 replay-ari-float" style={{ background: '#3B82F633', boxShadow: '0 0 14px #3B82F555' }}>
                <Sparkles className="w-5 h-5 text-blue-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-blue-300">
                  Ari · Step {idx + 1} of {FRAMES.length} · {frame.title}
                </div>
                <div className="text-white text-sm sm:text-[15px] leading-snug" data-testid="replay-narration">
                  {shown}{chars < frame.narration.length && <span className="inline-block w-1.5 h-4 ml-0.5 align-middle replay-caret bg-blue-300" />}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => setPlaying((p) => !p)} data-testid="replay-play-pause"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold text-white transition-transform hover:scale-105" style={{ background: '#3B82F6' }}>
                  {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}<span className="hidden sm:inline">{playing ? 'Pause' : 'Play'}</span>
                </button>
                <button onClick={restart} data-testid="replay-restart" className="p-2 rounded-full text-white/80 hover:bg-white/10 transition-colors" aria-label="Restart"><RotateCcw className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes replayAriFloat { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-3px) } }
        .replay-ari-float { animation: replayAriFloat 4s ease-in-out infinite }
        @keyframes replayCaret { 0%,100% { opacity: 1 } 50% { opacity: 0 } }
        .replay-caret { animation: replayCaret .9s step-end infinite }
      `}</style>
    </div>
  );
};

export default ReplayDemo;
